import React from 'react';
import { Button, TableCell, TableRow } from '@mui/material';
import { WEEK_LABELS } from '../constants/TrueViewTable.constants';

interface MoreWeeksButtonProps {
  onClick: () => void;
  showMore: boolean;
}

export const MoreWeeksButton: React.FC<MoreWeeksButtonProps> = ({ onClick, showMore }) => (
  <TableCell align="center" sx={{ p: 2 }}>
    <Button
      onClick={onClick}
      variant="contained"
      size="small"
      color={showMore ? "error" : "primary"}
      sx={{ fontSize: '12px', fontWeight: 600 }}
    >
      {showMore ? '← Hide' : 'More →'}
    </Button>
  </TableCell>
);

interface SubHeaderRowProps {
  showMoreWeeks: boolean;
}

export const SubHeaderRow: React.FC<SubHeaderRowProps> = ({ showMoreWeeks }) => {
  const generateWeekSubHeaders = (weekCount: number) => {
    const headers = [];
    for (let week = 0; week < weekCount; week++) {
      // Each week has 3 evaluations, each evaluation has 3 columns (Manager, Score, Date)
      for (let evalIndex = 0; evalIndex < 3; evalIndex++) {
        const isFirstEval = evalIndex === 0;
        const isLastEval = evalIndex === 2;
        
        headers.push(
          <TableCell 
            key={`${week}-${evalIndex}-manager`}
            align="center"
            sx={{
              p: 1,
              fontSize: '11px',
              fontWeight: 700,
              color: '#1f2937',
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
              backgroundColor: `hsl(${210 + evalIndex * 20}, 50%, 96%)`,
              borderLeft: isFirstEval ? '3px solid #3b82f6' : '1px solid #e5e7eb',
              minWidth: '60px'
            }}
          >
            Manager
          </TableCell>
        );
        headers.push(
          <TableCell 
            key={`${week}-${evalIndex}-score`}
            align="center"
            sx={{
              p: 1,
              fontSize: '11px',
              fontWeight: 700,
              color: '#1f2937',
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
              backgroundColor: `hsl(${210 + evalIndex * 20}, 50%, 96%)`,
              minWidth: '70px'
            }}
          >
            Score
          </TableCell>
        );
        headers.push(
          <TableCell 
            key={`${week}-${evalIndex}-date`}
            align="center"
            sx={{
              p: 1,
              fontSize: '11px',
              fontWeight: 700,
              color: '#1f2937',
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
              backgroundColor: `hsl(${210 + evalIndex * 20}, 50%, 96%)`,
              borderRight: isLastEval ? '3px solid #3b82f6' : '1px solid #e5e7eb',
              minWidth: '100px'
            }}
          >
            Date
          </TableCell>
        );
      }
    }
    return headers;
  };

  return (
    <TableRow>
      {generateWeekSubHeaders(showMoreWeeks ? 10 : 5)}
    </TableRow>
  );
};

interface WeekHeadersProps {
  showMoreWeeks: boolean;
  setShowMoreWeeks: (show: boolean) => void;
  getWeekDateRanges: () => string[];
}

export const WeekHeaders: React.FC<WeekHeadersProps> = ({ showMoreWeeks, setShowMoreWeeks, getWeekDateRanges }) => {
  const weekRanges = getWeekDateRanges();
  
  return (
    <>
      {!showMoreWeeks && (
        <MoreWeeksButton onClick={() => setShowMoreWeeks(true)} showMore={false} />
      )}
      {showMoreWeeks && (
        <>
          {WEEK_LABELS.slice(5).map((week, index) => (
            <TableCell 
              key={week} 
              colSpan={9} 
              align="center"
              sx={{
                p: 2.5,
                fontSize: '16px',
                fontWeight: 600,
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                minWidth: '400px'
              }}
            >
              {week} ({weekRanges[index + 5]})
            </TableCell>
          ))}
          <MoreWeeksButton onClick={() => setShowMoreWeeks(false)} showMore={true} />
        </>
      )}
    </>
  );
};